/**
 * HeroAgentFunnel — V141.3.
 *
 * Aspirational counterpart to RentAnAgentFunnel.
 *
 * Tone difference:
 *   • RentAnAgentFunnel = empathetic ("Spomínaš si, prečo si začal podnikať?")
 *   • HeroAgentFunnel   = heroic / aspirational ("Stojíš pred dverami. Otvor ich.")
 *
 * Used to A/B-test conversion. Both funnels link to the same agent
 * landing pages + onboarding flow. Different traffic sources can route
 * to whichever frame resonates (Meta Ads → emotional, LinkedIn →
 * heroic, paid B2B → comparison tables).
 *
 * Shares the agentCatalog data — zero duplication of agent rows.
 */
import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Crown, Shield, Sword, Zap, Trophy, Flame } from "lucide-react";
import { AGENTS } from "../data/agentCatalog";
import LegacyFunnelHub from "../components/LegacyFunnelHub";
import AIConciergeWidget from "../components/AIConciergeWidget";
import SocialProofTicker from "../components/SocialProofTicker";

// Heroic re-framings of the 14 agents — same data, different cape.
const HERO_LENS = {
  "review-reply":         { mantle: "Strážca tvojej reputácie",  weapon: "Empatia v 15 minútach" },
  "helpdesk":             { mantle: "Velmajster tvojho inboxu",  weapon: "0 nezvládnutých emailov" },
  "cart-recovery":        { mantle: "Lovec stratených peňazí",   weapon: "23–31% recovery rate" },
  "product-desc":         { mantle: "Pero, čo nezarmiti",        weapon: "1000 popisov za deň" },
  "newsletter":           { mantle: "Hlas tvojej značky",        weapon: "41% open rate" },
  "gdpr-watch":           { mantle: "Štít proti pokutám",        weapon: "€175k freedom" },
  "social-media":         { mantle: "DJ tvojej kampane",         weapon: "30-day content kalendár" },
  "pricing-intel":        { mantle: "Špión cien konkurencie",    weapon: "Margin guard 24/7" },
  "customer-onboarding":  { mantle: "Architekt prvého dojmu",    weapon: "47% activation" },
  "review-moderator":     { mantle: "Strelec proti hate-trollom",weapon: "99.2% accuracy" },
  "behavior-analytics":   { mantle: "Veštec konverzií",          weapon: "Heatmap + AI insights" },
  "product-importer":     { mantle: "Mostár medzi feedmi a shopom",weapon: "5 platforiem · 0 chýb" },
  "sales-hunter":         { mantle: "Fractional CEO",             weapon: "Zlaté prasiatka v matrixe" },
  "competitive-manager":  { mantle: "Strateg pre vojnu o #1",    weapon: "Priority-sorted ťahy" },
};

export default function HeroAgentFunnel() {
  const [active, setActive] = useState(0);
  const totals = useMemo(() => ({
    hours: AGENTS.reduce((s, a) => s + a.saves.time_hours_per_week, 0),
    eur: AGENTS.reduce((s, a) => s + a.saves.money_eur_per_month, 0),
  }), []);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="hero-agent-funnel">
      {/* ─── Hero ─── */}
      <section className="relative px-6 py-20 sm:py-32 border-b border-gray-900 overflow-hidden">
        {/* Decorative glow */}
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-amber-500 rounded-full blur-[160px] opacity-10 pointer-events-none" />
        <div className="max-w-5xl mx-auto relative z-10">
          <p className="text-[11px] uppercase tracking-[0.4em] font-black mb-6 text-amber-400" data-testid="hero-funnel-eyebrow">
            <Crown className="w-3 h-3 inline mr-2" />
            Pre tých, čo nečakajú · 14 AI agentov ti pripravujú trón
          </p>
          <h1 className="text-5xl sm:text-7xl lg:text-8xl font-black leading-[0.95] mb-8 tracking-tight">
            Staň sa <span className="text-amber-400">operátorom</span>,<br />
            ktorého sa <span className="text-red-400">konkurencia bojí</span>.
          </h1>
          <p className="text-xl sm:text-2xl text-gray-200 leading-relaxed mb-10 max-w-3xl">
            Hovorí sa, že najúspešnejší podnikatelia neraz povedia "delegujem to".<br />
            Tvoja armáda 14 AI agentov ti nikdy nepovie "zajtra".
          </p>

          {/* Heroic stat strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10 max-w-3xl">
            <div className="border border-amber-500/40 bg-amber-500/5 p-4">
              <Sword className="w-5 h-5 mb-2 text-amber-400" />
              <p className="text-[10px] text-amber-300 uppercase tracking-widest font-black mb-1">Tvoja armáda</p>
              <p className="text-3xl font-black">{AGENTS.length}</p>
            </div>
            <div className="border border-emerald-500/40 bg-emerald-500/5 p-4">
              <Zap className="w-5 h-5 mb-2 text-emerald-400" />
              <p className="text-[10px] text-emerald-300 uppercase tracking-widest font-black mb-1">Hodín / týž</p>
              <p className="text-3xl font-black text-emerald-400">{totals.hours}h</p>
            </div>
            <div className="border border-emerald-500/40 bg-emerald-500/5 p-4">
              <Trophy className="w-5 h-5 mb-2 text-emerald-400" />
              <p className="text-[10px] text-emerald-300 uppercase tracking-widest font-black mb-1">Vyhrané / mes</p>
              <p className="text-3xl font-black text-emerald-400">€{(totals.eur / 1000).toFixed(1)}k</p>
            </div>
            <div className="border border-red-500/40 bg-red-500/5 p-4">
              <Flame className="w-5 h-5 mb-2 text-red-400" />
              <p className="text-[10px] text-red-300 uppercase tracking-widest font-black mb-1">Trial</p>
              <p className="text-3xl font-black">14d</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <Link to="/agent-os/onboard" data-cta
              data-testid="hero-funnel-primary-cta"
              className="inline-flex items-center gap-2 bg-amber-500 text-black font-black px-10 py-5 text-base uppercase tracking-widest hover:bg-amber-400 transition-all hover:scale-105">
              Aktivuj svoju armádu <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/prenajmi-si-agenta"
              className="inline-flex items-center gap-2 border border-white text-white font-black px-10 py-5 text-base uppercase tracking-widest hover:bg-white hover:text-black">
              Empathetic verzia →
            </Link>
          </div>
        </div>
      </section>

      {/* ─── 3-act hero narrative ─── */}
      <section className="px-6 py-4 border-b border-gray-900">
        <div className="max-w-4xl mx-auto">
          <SocialProofTicker />
        </div>
      </section>
      <section className="px-6 py-20 border-b border-gray-900">
        <div className="max-w-4xl mx-auto">
          <p className="text-[11px] uppercase tracking-[0.3em] font-black text-amber-400 mb-6">
            <Shield className="w-3 h-3 inline mr-2" />
            Tvoj hero arc — 3 akty
          </p>

          <div className="space-y-12">
            <div className="border-l-4 border-amber-500 pl-6">
              <p className="text-[10px] uppercase tracking-widest text-gray-500 font-black mb-2">Akt 1 · Volanie</p>
              <h3 className="text-3xl sm:text-4xl font-black mb-3 leading-tight">Stojíš pred dverami. Otvor ich.</h3>
              <p className="text-base text-gray-300 leading-relaxed">
                Každý hrdina začne pochybnosťou. "Nemám čas." "Je to draho." "Možno zajtra." Ale ty si tu. Čítaš ďalej.<br />
                Tvoja minulá verzia by neuverila, koľko zmôžeš v ďalších 14 dňoch. Tvoja budúca verzia ti nikdy neodpustí, že si zaváhal.
              </p>
            </div>

            <div className="border-l-4 border-emerald-500 pl-6">
              <p className="text-[10px] uppercase tracking-widest text-gray-500 font-black mb-2">Akt 2 · Tréning</p>
              <h3 className="text-3xl sm:text-4xl font-black mb-3 leading-tight">14 spojencov. Každý so svojou zbraňou.</h3>
              <p className="text-base text-gray-300 leading-relaxed">
                Strážca reputácie. Velmajster inboxu. Lovec stratených peňazí. Tvoja armáda nikdy nespí, nečíta noviny, nehádka sa s manželkou.<br />
                Ty si veliteľ. Oni sú tvoje pluky. Stačí povedať "začnite".
              </p>
            </div>

            <div className="border-l-4 border-red-500 pl-6">
              <p className="text-[10px] uppercase tracking-widest text-gray-500 font-black mb-2">Akt 3 · Návrat</p>
              <h3 className="text-3xl sm:text-4xl font-black mb-3 leading-tight">Konkurencia uvidí len výsledky. Nie tvoju cestu.</h3>
              <p className="text-base text-gray-300 leading-relaxed">
                O 90 dní budú pýtať, "ako si to urobil?". A ty sa usmeješ. Lebo vieš, že hrdinstvo nie je o sile.<br />
                Hrdinstvo je o tom, vedieť, koho si k sebe pribrať.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── 14 spojenci grid (heroic frame) ─── */}
      <section className="px-6 py-20 border-b border-gray-900 bg-neutral-950">
        <div className="max-w-6xl mx-auto">
          <p className="text-[11px] uppercase tracking-[0.3em] font-black text-amber-400 mb-3">
            <Crown className="w-3 h-3 inline mr-2" />
            Tvoja armáda
          </p>
          <h2 className="text-4xl sm:text-5xl font-black mb-3">14 spojencov pripravených na tvoj rozkaz.</h2>
          <p className="text-base text-gray-400 mb-10 max-w-2xl">
            Klikni na ktorýkoľvek a uvidíš jeho mantlu + zbraň. Detail v new tabe.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="hero-funnel-agent-grid">
            {AGENTS.map((a, i) => {
              const lens = HERO_LENS[a.slug] || {};
              const isActive = active === i;
              return (
                <div key={a.slug}
                  onMouseEnter={() => setActive(i)}
                  className={`border p-5 transition-all cursor-pointer ${
                    isActive ? "border-amber-500 bg-amber-500/5 scale-[1.02]" : "border-gray-800"
                  }`}
                  data-testid={`hero-funnel-card-${a.slug}`}
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className="text-4xl">{a.emoji}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] uppercase tracking-widest font-black text-amber-400 mb-1">
                        {lens.mantle || "AI Agent"}
                      </p>
                      <h3 className="font-black text-base leading-tight">{a.name}</h3>
                    </div>
                  </div>
                  <p className="text-[11px] text-emerald-400 font-bold mb-2">
                    ⚔️ {lens.weapon || a.tagline}
                  </p>
                  <p className="text-sm text-gray-300 leading-relaxed mb-3 line-clamp-2">{a.tagline}</p>
                  <Link to={`/agent/${a.slug}`} data-cta
                    className="text-[11px] text-amber-400 font-bold hover:underline inline-flex items-center gap-1">
                    Detail spojenca <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── Battle plan CTA ─── */}
      <section className="relative px-6 py-24 overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-red-500 rounded-full blur-[160px] opacity-10 pointer-events-none" />
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <p className="text-[11px] uppercase tracking-[0.4em] font-black text-red-400 mb-6">
            <Flame className="w-3 h-3 inline mr-2" />
            Rozhodni sa
          </p>
          <p className="text-4xl sm:text-5xl font-black leading-tight mb-6">
            Dve cesty.<br />
            <span className="text-gray-500">Tichá rezignácia.</span><br />
            alebo<br />
            <span className="text-amber-400">Hlasité víťazstvo.</span>
          </p>
          <p className="text-base text-gray-300 leading-relaxed mb-3">
            Tvoja konkurencia ti práve tlačí jeden meter pred nos.<br />
            Tvoj zákazník ti práve hľadá náhradu.<br />
            Tvoja schránka ti práve hromadí 47 nezvládnutých vecí.
          </p>
          <p className="text-lg text-amber-400 font-black mb-10">
            👉 Nech to zajtra robí niekto iný. Aktivuj svoju armádu dnes.
          </p>

          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/agent-os/onboard" data-cta
              data-testid="hero-funnel-final-cta"
              className="inline-flex items-center gap-2 bg-amber-500 text-black font-black px-12 py-5 text-base uppercase tracking-widest hover:bg-amber-400 hover:scale-105 transition-all">
              Áno, idem do boja <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <p className="text-[11px] text-gray-500 mt-8 max-w-md mx-auto">
            14 dní zadarmo · bez karty · €79–€129/mes podľa agenta · Bundle 3-za-2 = €198/mes
          </p>
        </div>
      </section>

      {/* ─── Legacy funnels ─── */}
      <LegacyFunnelHub title="Ďalšie zbrane vo zbrojnici" />

      {/* ─── AI Concierge ─── */}
      <AIConciergeWidget />
    </div>
  );
}
